// src/pages/EngineeringPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiBriefcase, HiArrowRight, HiLocationMarker } from 'react-icons/hi';
import { openPositions } from '../data/jobsData';
import { useApplicationForm } from '../hooks/useApplicationForm';
import ApplicationFormModal from '../components/ApplicationFormModal';

const EngineeringPage = () => {
  const { showForm, setShowForm, formData, handleChange, resetForm } = useApplicationForm();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const positions = openPositions.filter(pos => pos.department === "Engineering");

  const handleApplyClick = () => {
    setShowForm(true);
  };

  return (
    <div className="pb-20 pt-2 bg-white">
      <ApplicationFormModal showForm={showForm} setShowForm={setShowForm} formData={formData} handleChange={handleChange} resetForm={resetForm} />
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500 py-32 overflow-hidden">
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
          animate={{ y: [0, 100, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{ y: [0, -100, 0], scale: [1, 1.3, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        <div className="container-custom relative z-10 text-center">
          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 drop-shadow-lg">
            Engineering at EFA Tech Solutions
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-12 leading-relaxed">
            Join our Engineering team and help build the future of technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={handleApplyClick}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary-600 px-10 py-4 rounded-xl font-bold shadow-2xl hover:shadow-white/20 flex items-center justify-center"
            >
              <HiBriefcase className="w-6 h-6 mr-2" />
              Apply Now
            </motion.button>

          </div>
        </div>
      </div>

      {/* Mission / value */}
      <section className="py-20 bg-gradient-to-b from-white to-neutral-50">
        <div className="container-custom text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why join our Engineering team?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We build scalable, reliable systems used globally. You’ll be empowered to make real decisions, work in cross-functional teams, and deliver value at speed.
          </p>
        </div>
        <div className="container-custom px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Example benefit cards */}
            {[
              { icon: '💡', title: 'Innovative Projects', desc: 'Work on new features, microservices and platforms that drive our business.' },
              { icon: '🌍', title: 'Global Impact', desc: 'Your code will support US clients and markets around the world.' },
              { icon: '⏰', title: 'Night-Shift Benefits', desc: 'Special roles with US timezone night-shift offer additional compensation & remote flexibility.' },
            ].map((b, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-5xl mb-4">{b.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{b.title}</h3>
                <p className="text-gray-600 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Open Engineering Positions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">We currently have the following opportunity:</p>
        </div>
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto px-4">
            {positions.map((pos, idx) => (
              <motion.div
                key={pos.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -6, boxShadow: "0 15px 30px rgba(0,0,0,0.1)" }}
                className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Header */}
                <div className="mb-3">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <HiBriefcase className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-primary-600">
                      Engineering & Development
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                    {pos.title}
                  </h3>
                </div>

                {/* Meta Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className="px-2.5 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-semibold">
                    Full-Time
                  </span>
                  <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                    Remote
                  </span>
                  <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold">
                    Night Shift
                  </span>
                </div>

                {/* Location */}
                <div className="flex items-start text-xs text-gray-600 mb-4">
                  <HiLocationMarker className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>{pos.location}</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm mb-4 leading-relaxed flex-grow line-clamp-2">
                  {pos.description}
                </p>

                {/* Key Skills */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Key Skills:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {pos.requirements.slice(0, 3).map((req, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 rounded text-gray-700 text-xs font-medium"
                      >
                        {req}
                      </span>
                    ))}
                    {pos.requirements.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 rounded text-gray-600 text-xs font-medium">
                        +{pos.requirements.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Apply Button */}
                <motion.button
                  onClick={handleApplyClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200"
                >
                  Apply Now <HiArrowRight className="w-4 h-4" />
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / Next Steps */}
      <section className="py-20 bg-gradient-to-b from-white to-neutral-50">
        <div className="container-custom text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            What happens after you apply?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Once you submit your application, our recruitment team will review it, and if you’re a match you’ll receive an invite to our technical assessment. We appreciate your interest and aim to provide feedback as quickly as possible.
          </p>
        </div>
      </section>
    </div>
  );
};

export default EngineeringPage;
